import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import { Info, Plus } from 'lucide-react'
import { Modal } from './Modal'
import { QuestionBankBrowser } from './QuestionBankBrowser'
import type { BankItem } from './types'

type AddFromBankPickerProps = {
  open: boolean
  onClose: () => void
  /** Receives the chosen bank items (copied) for the editor to append. */
  onInsert: (items: BankItem[]) => void
}

/**
 * Modal launched from the quiz editor's "إضافة من بنك الأسئلة" button. Reuses
 * {@link QuestionBankBrowser} in `picker` mode for multi-select, then maps the
 * chosen items into editor questions on confirm (copy-on-insert — handled by
 * the caller). Built on the shared {@link Modal} (Radix dialog) for focus
 * trap / `Esc` / focus restore; the selected-count is announced via
 * `aria-live`.
 *
 * Selection is keyed by item id and stores the full {@link BankItem}, so it
 * survives paging/filtering inside the browser (the browser only holds the
 * current page) and gives the insert handler complete payloads to copy.
 */
export function AddFromBankPicker({
  open,
  onClose,
  onInsert,
}: AddFromBankPickerProps) {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const [selected, setSelected] = useState<Map<string, BankItem>>(new Map())

  // Start every open with a clean selection.
  useEffect(() => {
    if (open) setSelected(new Map())
  }, [open])

  function toggle(item: BankItem) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(item.id)) next.delete(item.id)
      else next.set(item.id, item)
      return next
    })
  }

  const selectedIds = new Set(selected.keys())
  const count = selected.size

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('bankPickerTitle')}
      size="lg"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-1.5 text-xs text-brand-600 leading-relaxed">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t('bankCopyNote')}</span>
          </p>
          <div className="flex items-center justify-end gap-3">
            <span
              className="text-sm font-semibold text-brand-700 tabular-nums"
              aria-live="polite"
            >
              {t('bankPickerSelectedCount', { count })}
            </span>
            <Button type="button" variant="ghost" onClick={onClose}>
              {tCommon('cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => onInsert(Array.from(selected.values()))}
              disabled={count === 0}
              className="gap-2 bg-gradient-to-bl from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>{t('bankPickerInsert')}</span>
            </Button>
          </div>
        </div>
      }
    >
      <QuestionBankBrowser
        mode="picker"
        selectedIds={selectedIds}
        onToggleSelect={toggle}
      />
    </Modal>
  )
}
